import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CheckIn, User, sellerDisplayName } from '../../types';
import { useAccessScope } from '../../hooks/useAccessScope';
import 'leaflet/dist/leaflet.css';

type Pt = { lat: number; lng: number; t: number };
type Stop = { lat: number; lng: number; name: string; arrivedAt: Date; leftAt: Date | null; order: number };

const fmtHora = (t: number) => new Date(t).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const fmtDur = (ms: number) => { const m = Math.round(ms / 60000); return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}min`; };

// Posição interpolada ao longo do caminho no instante t.
function posAt(path: Pt[], t: number): [number, number] | null {
  if (!path.length) return null;
  if (t <= path[0].t) return [path[0].lat, path[0].lng];
  const last = path[path.length - 1];
  if (t >= last.t) return [last.lat, last.lng];
  for (let i = 0; i < path.length - 1; i++) {
    if (t >= path[i].t && t <= path[i + 1].t) {
      const span = path[i + 1].t - path[i].t || 1;
      const f = (t - path[i].t) / span;
      return [path[i].lat + (path[i + 1].lat - path[i].lat) * f, path[i].lng + (path[i + 1].lng - path[i].lng) * f];
    }
  }
  return [last.lat, last.lng];
}

function FitBounds({ pts }: { pts: [number, number][] }) {
  const map = useMap();
  useEffect(() => { if (pts.length) { try { map.fitBounds(pts as any, { padding: [40, 40], maxZoom: 16 }); } catch { /* ignore */ } } }, [pts, map]);
  return null;
}

const SPEEDS = [60, 300, 1200]; // fator de aceleração da reprodução

export default function ReplayPage() {
  const scope = useAccessScope();
  const [sellers, setSellers] = useState<User[]>([]);
  const [sellerId, setSellerId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(false);
  const [playT, setPlayT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(300);
  const tick = useRef<any>(null);

  useEffect(() => {
    getDocs(query(collection(db, 'users'), orderBy('name'))).then((s) => {
      setSellers(s.docs.map((d) => ({ id: d.id, ...d.data() })) as User[]);
    });
  }, []);

  // TODOS os usuários podem navegar/fazer check-in (não só vendedores) — o seletor lista todos:
  // acesso total vê todos; gestor vê a equipe dele + ele mesmo (scope.sellerIds).
  const sellerOptions = useMemo(() => {
    if (scope.full) return sellers;
    const ids = new Set(scope.sellerIds || []);
    return sellers.filter((s) => ids.has(s.id));
  }, [sellers, scope]);

  const carregar = async () => {
    if (!sellerId) return;
    setLoading(true); setPlaying(false);
    try {
      const start = new Date(date + 'T00:00:00');
      const end = new Date(date + 'T23:59:59');
      const snap = await getDocs(query(
        collection(db, 'checkins'),
        where('sellerId', '==', sellerId),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end),
        orderBy('createdAt', 'asc'),
      ));
      setCheckins(snap.docs.map((d) => {
        const r = d.data();
        return { id: d.id, ...r, createdAt: r.createdAt?.toDate() ?? new Date(), checkoutAt: r.checkoutAt?.toDate?.() ?? null } as CheckIn;
      }));
    } finally { setLoading(false); }
  };

  // Monta o caminho (todos os trajetos + pontos de check-in) e as paradas do dia.
  const { path, stops } = useMemo(() => {
    const path: Pt[] = [];
    const stops: Stop[] = [];
    for (const ci of checkins) {
      const trail = ci.trajeto?.trail || [];
      for (const p of trail) if (p.t) path.push({ lat: p.lat, lng: p.lng, t: p.t });
      if (ci.location?.latitude != null) {
        path.push({ lat: ci.location.latitude, lng: ci.location.longitude, t: ci.createdAt.getTime() });
        stops.push({ lat: ci.location.latitude, lng: ci.location.longitude, name: ci.clientName, arrivedAt: ci.createdAt, leftAt: ci.checkoutAt ?? null, order: stops.length + 1 });
      }
    }
    path.sort((a, b) => a.t - b.t);
    return { path, stops };
  }, [checkins]);

  const tMin = path.length ? path[0].t : 0;
  const tMax = path.length ? path[path.length - 1].t : 0;

  useEffect(() => { setPlayT(tMin); setPlaying(false); }, [tMin, tMax]);

  useEffect(() => {
    if (!playing) { if (tick.current) clearInterval(tick.current); return; }
    tick.current = setInterval(() => {
      setPlayT((prev) => {
        const next = prev + 200 * speed; // avança 200ms×fator a cada 200ms reais
        if (next >= tMax) { setPlaying(false); return tMax; }
        return next;
      });
    }, 200);
    return () => { if (tick.current) clearInterval(tick.current); };
  }, [playing, speed, tMax]);

  const linePts = path.map((p) => [p.lat, p.lng] as [number, number]);
  const cursor = posAt(path, playT);
  const center: [number, number] = cursor || (linePts[0] || [-15.78, -47.93]);
  const temReplay = path.length > 1;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Replay do dia</h1>
          <p style={styles.subtitle}>Reveja o trajeto do vendedor ao longo do dia (por onde passou, onde parou e por quanto tempo).</p>
        </div>
      </div>

      <div style={styles.controls}>
        <select style={styles.input} value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
          <option value="">Selecione o usuário...</option>
          {sellerOptions.map((s) => <option key={s.id} value={s.id}>{sellerDisplayName(s)}</option>)}
        </select>
        <input type="date" style={styles.input} value={date} onChange={(e) => setDate(e.target.value)} />
        <button style={styles.loadBtn} onClick={carregar} disabled={!sellerId || loading}>{loading ? 'Carregando...' : 'Carregar'}</button>
      </div>

      {checkins.length === 0 ? (
        <div style={styles.empty}>Selecione o vendedor e a data e clique em <strong>Carregar</strong>.</div>
      ) : !temReplay ? (
        <div style={styles.empty}>Este dia não tem trajeto gravado (só há check-ins sem rastreamento). {stops.length} parada(s).</div>
      ) : (
        <>
          <div style={styles.mapWrapper}>
            <MapContainer center={center} zoom={14} style={{ height: 520, width: '100%' }}>
              <OsmTiles />
              <FitBounds pts={linePts} />
              <Polyline positions={linePts} pathOptions={{ color: '#f5821f', weight: 4, opacity: 0.85 }} />
              {stops.map((s) => (
                <CircleMarker key={s.order} center={[s.lat, s.lng]} radius={9} pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}>
                  <Tooltip direction="top">
                    <strong>{s.order}. {s.name}</strong><br />
                    Chegou {fmtHora(s.arrivedAt.getTime())}{s.leftAt ? ` · saiu ${fmtHora(s.leftAt.getTime())} (${fmtDur(s.leftAt.getTime() - s.arrivedAt.getTime())})` : ''}
                  </Tooltip>
                </CircleMarker>
              ))}
              {cursor && (
                <CircleMarker center={cursor} radius={11} pathOptions={{ color: '#fff', weight: 3, fillColor: '#16a34a', fillOpacity: 1 }}>
                  <Tooltip permanent direction="top">{fmtHora(playT)}</Tooltip>
                </CircleMarker>
              )}
            </MapContainer>
          </div>

          <div style={styles.player}>
            <button style={styles.playBtn} onClick={() => { if (playT >= tMax) setPlayT(tMin); setPlaying((p) => !p); }}>
              {playing ? '⏸️ Pausar' : '▶️ Reproduzir'}
            </button>
            <span style={styles.clock}>{fmtHora(playT)}</span>
            <input type="range" min={tMin} max={tMax} value={playT} onChange={(e) => { setPlaying(false); setPlayT(Number(e.target.value)); }} style={{ flex: 1 }} />
            <span style={styles.range}>{fmtHora(tMin)}–{fmtHora(tMax)}</span>
            <select style={styles.speedSel} value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
              {SPEEDS.map((s) => <option key={s} value={s}>{s}×</option>)}
            </select>
          </div>

          <div style={styles.summary}>
            <strong>{stops.length}</strong> parada(s) · das <strong>{fmtHora(tMin)}</strong> às <strong>{fmtHora(tMax)}</strong>
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  controls: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  input: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff' },
  loadBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  empty: { background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', color: '#999', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  mapWrapper: { borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 12px rgba(0,0,0,0.08)' },
  player: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, background: '#fff', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  playBtn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' },
  clock: { fontWeight: 700, color: '#1a1a2e', fontSize: 16, minWidth: 54, textAlign: 'center' },
  range: { fontSize: 12, color: '#888', whiteSpace: 'nowrap' },
  speedSel: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '6px 8px', fontSize: 13, background: '#fff' },
  summary: { marginTop: 10, color: '#444', fontSize: 14 },
};
